<script setup lang="ts">
import { RouterView } from 'vue-router'
import { watch, ref } from 'vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useSessionManager } from '@/composables/useSessionManager'
import { useSyncFailureNotifier } from '@/composables/useSyncFailureNotifier'
import { usePwaUpdate } from '@/composables/usePwaUpdate'
import SessionWarning from '@/components/ui/SessionWarning.vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import HelpButton from '@/components/help/HelpButton.vue'
import HelpModal from '@/components/help/HelpModal.vue'
import CatalogueExterneButton from '@/components/catalogue/CatalogueExterneButton.vue'

const authStore = useAuthStore()
const {
  showExpirationWarning,
  timeUntilExpiration,
  startSessionMonitoring,
  stopSessionMonitoring,
  extendSession,
  logout
} = useSessionManager()

// PWA auto-update with conditional reload (mount / 5min background / quote:saved)
usePwaUpdate()

// Story 6-5: Sync failure notifier (global)
// Initialize only once when authenticated
const syncNotifierCleanup = ref<(() => void) | null>(null)

// Start/stop monitoring based on auth state
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      startSessionMonitoring()
      // Start sync failure notifier
      if (!syncNotifierCleanup.value) {
        const { cleanup } = useSyncFailureNotifier()
        syncNotifierCleanup.value = cleanup
      }
    } else {
      stopSessionMonitoring()
      // Stop sync failure notifier
      if (syncNotifierCleanup.value) {
        syncNotifierCleanup.value()
        syncNotifierCleanup.value = null
      }
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="min-h-screen bg-vfr-light">
    <RouterView />

    <!-- Session Expiration Warning -->
    <SessionWarning
      :show="showExpirationWarning"
      :time-remaining="timeUntilExpiration"
      @extend="extendSession"
      @logout="logout"
    />

    <!-- Toast Notifications -->
    <ToastContainer />

    <!-- Catalogues externes -->
    <CatalogueExterneButton v-if="authStore.isAuthenticated" />

    <!-- Help System -->
    <HelpButton />
    <HelpModal />
  </div>
</template>

<style scoped>
/* App-level styles */
</style>
